import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import apiClient from '@/api/client'
import type {
  Expense,
  PnLReport,
  CurrentDayStatus,
  ExpenseIntelligenceReport,
  ExpenseSummary,
  MetaData,
} from '@/types'
import { cn, formatDateRangeHuman } from '@/lib/utils'
import { toastHelpers } from '@/lib/toast-helpers'
import { getStoredUserRole } from '@/lib/staff-roles'
import { rangeToSearch, useReportRange } from '@/hooks/useReportRange'
import { DateRangeFilter } from '@/components/admin/reports/DateRangeFilter'
import { Skeleton } from '@/components/ui/skeleton'
import { ExpenseOverviewTab } from '@/components/admin/expenses/ExpenseOverviewTab'
import { ExpenseLedgerTab, type ExpenseLedgerSortKey } from '@/components/admin/expenses/ExpenseLedgerTab'
import { ExpensePnLTab } from '@/components/admin/expenses/ExpensePnLTab'
import { AddExpenseDialog, CorrectPaidOutDialog, EditExpenseDialog } from '@/components/admin/expenses/ExpenseDialogs'
import { ReceiptScanButton } from '@/components/receipts/ReceiptScanButton'
import { ReceiptReviewSheet } from '@/components/receipts/ReceiptReviewSheet'
import { useReceiptScanEnabled } from '@/hooks/useReceiptScanEnabled'
import { ManageExpenseCategoriesDialog } from '@/components/admin/expenses/ManageExpenseCategoriesDialog'
import { ExpenseExportMenu } from '@/components/admin/expenses/ExpenseExportMenu'
import { resolveGranularity } from '@/components/admin/expenses/expenseDashboardLogic'
import type { ExpenseTab, ExpensesSearch } from '@/components/admin/expenses/expensesRouteSearch'
import { BarChart3, Receipt, TrendingUp } from 'lucide-react'

/**
 * Which tabs a role may open — mirrors the backend split (F-RPT-N34/N35):
 *   admin      → everything.
 *   manager    → the ledger only (expenseLedgerWrite); no P&L, and the
 *                Overview reads P&L-derived tiles so it goes too.
 *   accountant → ledger (read-only), P&L, and — since F-FE-N129 — the
 *                Overview, which now reads only pnlRead / ledger-read data
 *                (today's live strip is admin-only and simply not requested).
 */
function tabsForRole(role: string): ExpenseTab[] {
  switch (role) {
    case 'manager':
      return ['ledger']
    case 'accountant':
      return ['overview', 'ledger', 'pnl']
    default:
      return ['overview', 'ledger', 'pnl']
  }
}

/**
 * Expense dashboard (F-FE-N129). One period bar — the same presets and
 * calendar as Reports — drives every tab, and the tab, the ledger's category
 * and the period live in the URL so every number on Overview, the P&L and
 * Reports can deep-link into exactly the rows behind it.
 */
export function ExpenseDashboard() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const search = useSearch({ from: '/admin/expenses' }) as ExpensesSearch
  const role = getStoredUserRole()
  const allowedTabs = useMemo(() => tabsForRole(role), [role])
  const isAdmin = role === 'admin'
  const ledgerReadOnly = role === 'accountant'
  const canSeeToday = role === 'admin'

  const range = useReportRange('this_month')
  const periodLabel = formatDateRangeHuman(range.from, range.to)

  // ── Tab + category are URL state (validated in routes/admin/expenses.tsx) ─
  const requestedTab = search.tab && allowedTabs.includes(search.tab) ? search.tab : undefined
  /**
   * Derived, never held in state (F-FE-N146). This used to be `useState` seeded
   * from the URL plus an effect re-syncing it, which could not diverge only
   * because every mutator happened to write both — and `setCategory` already
   * did not, staying correct purely because it is passed to the Ledger tab
   * alone, where the tab is already 'ledger'. One new call site from Overview
   * would have reproduced the Reports drill bug exactly. Deriving it makes that
   * unrepresentable instead of accidental.
   */
  const tab: ExpenseTab = requestedTab ?? allowedTabs[0]

  const category = search.category ?? ''
  // Every navigation re-stamps the period: the range hook writes it with
  // history.replaceState, which the router does not see, so `prev` alone
  // would drop it from the URL.
  const go = (patch: Partial<ExpensesSearch>) =>
    navigate({
      to: '/admin/expenses',
      search: prev => ({ ...(prev as ExpensesSearch), ...rangeToSearch(range), ...patch }),
      replace: true,
    })
  const setTab = (t: ExpenseTab) => go({ tab: t })
  const setCategory = (c: string) => go({ tab: 'ledger', category: c || undefined })
  const drillCategory = (slug: string) => go({ tab: 'ledger', category: slug })
  const openLedger = () => setTab('ledger')
  const openPnL = () => setTab('pnl')

  // ── Overview ──────────────────────────────────────────────────────────
  const { data: currentDay, isLoading: dayLoading } = useQuery({
    queryKey: ['currentDayStatus'],
    queryFn: () => apiClient.getCurrentDayStatus(),
    staleTime: 30_000,
    enabled: canSeeToday && tab === 'overview',
  })
  const { data: overviewPnLRes, isFetching: overviewPnLLoading } = useQuery({
    queryKey: ['pnlReport', 'overview', range.fromISO, range.toISO],
    queryFn: () => apiClient.getPnLReport({ period: 'daily', from: range.fromISO, to: range.toISO }),
    enabled: tab === 'overview',
    staleTime: 60_000,
  })
  const { data: overviewSummaryRes, isFetching: overviewSummaryLoading } = useQuery({
    queryKey: ['expenseSummary', 'overview', range.fromISO, range.toISO],
    queryFn: () => apiClient.getExpenseSummary({ from: range.fromISO, to: range.toISO }),
    enabled: tab === 'overview',
    staleTime: 60_000,
  })
  const { data: intelRes, isFetching: intelLoading } = useQuery({
    queryKey: ['expenseIntelligence', range.fromISO, range.toISO],
    queryFn: () => apiClient.getExpenseIntelligence({ from: range.fromISO, to: range.toISO }),
    enabled: tab === 'overview',
    staleTime: 60_000,
  })

  // ── Ledger ────────────────────────────────────────────────────────────
  const [expPage, setExpPage] = useState(1)
  const [expMethod, setExpMethod] = useState('')
  const [expSearchInput, setExpSearchInput] = useState('')
  const [debouncedExpSearch, setDebouncedExpSearch] = useState('')
  const [sortBy, setSortBy] = useState<ExpenseLedgerSortKey>('expense_date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedExpSearch(expSearchInput.trim()), 300)
    return () => window.clearTimeout(id)
  }, [expSearchInput])
  useEffect(() => {
    setExpPage(1)
  }, [debouncedExpSearch, category, range.fromISO, range.toISO])

  const { data: expensesRes, isFetching: expLoading } = useQuery({
    queryKey: ['expenses', expPage, category, range.fromISO, range.toISO, debouncedExpSearch, expMethod, sortBy, sortDir],
    queryFn: () =>
      apiClient.getExpenses({
        page: expPage,
        per_page: 15,
        category: category || undefined,
        from: range.fromISO,
        to: range.toISO,
        search: debouncedExpSearch || undefined,
        payment_method: expMethod || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      }),
    enabled: tab === 'ledger',
  })
  // Chips + totals for the same period / search / method — deliberately
  // without the category, so the chips stay put while one is selected.
  const { data: ledgerSummaryRes } = useQuery({
    queryKey: ['expenseSummary', 'ledger', range.fromISO, range.toISO, debouncedExpSearch, expMethod],
    queryFn: () =>
      apiClient.getExpenseSummary({
        from: range.fromISO,
        to: range.toISO,
        search: debouncedExpSearch || undefined,
        payment_method: expMethod || undefined,
      }),
    enabled: tab === 'ledger',
    staleTime: 30_000,
  })

  // ── P&L ───────────────────────────────────────────────────────────────
  // F-FE-N141: the grain follows the period bar — a one-day range opens on
  // Hourly, a year on Monthly — and an operator's choice is kept while the new
  // range still supports it.
  const [pnlPeriodChoice, setPnlPeriodChoice] = useState('')
  const pnlPeriod = resolveGranularity(pnlPeriodChoice, range.days)
  const { data: pnlRes, isFetching: pnlLoading } = useQuery({
    queryKey: ['pnlReport', 'main', pnlPeriod, range.fromISO, range.toISO],
    queryFn: () => apiClient.getPnLReport({ period: pnlPeriod, from: range.fromISO, to: range.toISO }),
    enabled: tab === 'pnl',
    staleTime: 30_000,
  })

  // ── Dialogs ───────────────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false)
  // F-RPT-N43: the receipt open in the review sheet.
  const receiptScan = useReceiptScanEnabled()
  const [reviewScanId, setReviewScanIdState] = useState<string | null>(search.receipt ?? null)
  // The open receipt is mirrored to ?receipt= so the link can be shared.
  const setReviewScanId = (id: string | null) => {
    setReviewScanIdState(id)
    go({ receipt: id ?? undefined })
  }
  const [editExpense, setEditExpense] = useState<Expense | null>(null)
  const [correctExpense, setCorrectExpense] = useState<Expense | null>(null)
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false)
  // F-CTRL-N64: category DEFINITIONS are admin-only on the backend; hide the
  // entry points from everyone else rather than render a guaranteed 403.

  const deleteExpenseMut = useMutation({
    mutationFn: (id: string) => apiClient.deleteExpense(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses'] })
      qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
      qc.invalidateQueries({ queryKey: ['pnlReport'] })
      qc.invalidateQueries({ queryKey: ['expenseSummary'] })
      qc.invalidateQueries({ queryKey: ['dailyClosings'] })
      qc.invalidateQueries({ queryKey: ['expenseIntelligence'] })
      toastHelpers.success('Expense deleted')
    },
    onError: (err: Error) => toastHelpers.apiError('Delete expense', err),
  })

  const dayStatus = currentDay?.data as CurrentDayStatus | undefined
  const expenses = (expensesRes as { data?: Expense[] })?.data
  const expMeta = (expensesRes as { meta?: MetaData })?.meta
  const ledgerSummary = ledgerSummaryRes?.data as ExpenseSummary | undefined
  const pnl = pnlRes?.data as PnLReport | undefined
  const overviewPnL = overviewPnLRes?.data as PnLReport | undefined
  const overviewSummary = overviewSummaryRes?.data as ExpenseSummary | undefined
  const intel = intelRes?.data as ExpenseIntelligenceReport | undefined

  const tabs: { key: ExpenseTab; label: string; icon: ReactNode }[] = (
    [
      { key: 'overview', label: 'Overview', icon: <BarChart3 className="h-4 w-4" /> },
      { key: 'ledger', label: 'Ledger', icon: <Receipt className="h-4 w-4" /> },
      { key: 'pnl', label: 'P&L', icon: <TrendingUp className="h-4 w-4" /> },
    ] as { key: ExpenseTab; label: string; icon: ReactNode }[]
  ).filter(t => allowedTabs.includes(t.key))

  const showOverviewSkeleton =
    tab === 'overview' &&
    ((canSeeToday && dayLoading) || overviewPnLLoading || overviewSummaryLoading || intelLoading) &&
    !overviewPnL &&
    !overviewSummary

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* F-FE-N164: the Reports page's header, exactly — title and period bar
          on one band, tabs and Export on the next. Export used to share the
          period bar's row, which pushed the pair onto three lines beside a
          two-line subtitle on any laptop. */}
      <div className="@container">
        <div className="flex flex-col gap-4 @[78rem]:flex-row @[78rem]:items-start @[78rem]:justify-between">
          <div className="min-w-0">
            <h1 className="whitespace-nowrap text-3xl font-bold tracking-tight">Expense Dashboard</h1>
            <p className="mt-1 text-muted-foreground">
              {ledgerReadOnly
                ? 'Read-only view of the expense ledger and the profit and loss'
                : 'Spending, and the profit and loss it leads to'}
            </p>
          </div>
          {/* Sized by the room the page has, not the viewport: the sidebar can
              take 256px of it, so a viewport breakpoint guessed wrong. */}
          <div className="@[78rem]:shrink-0 @[78rem]:pt-1">
            <DateRangeFilter range={range} />
          </div>
        </div>
        {/* F-FE-N147: no "Daily closing" button here. It duplicated the
            sidebar item outright — same label, same lock icon, same route —
            and it is cross-page navigation rather than an expenses action.
            The one door to Day Close is the contextual button on the today
            strip, beside the figures it acts on. Nobody lost access: both
            roles that saw this already carry `day-close` in their sidebar
            sections (`staff-roles.ts`). */}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
          {tabs.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                tab === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
        <ExpenseExportMenu
          tab={tab}
          from={range.fromISO}
          to={range.toISO}
          canExportPnL={allowedTabs.includes('pnl')}
          pnlPeriod={pnlPeriod}
          ledgerFilters={{
            category: category || undefined,
            search: debouncedExpSearch || undefined,
            payment_method: expMethod || undefined,
          }}
        />
      </div>

      {tab === 'overview' &&
        (showOverviewSkeleton ? (
          <div className="space-y-6">
            <Skeleton className="h-[116px]" />
            <Skeleton className="h-40" />
            <Skeleton className="h-[480px]" />
          </div>
        ) : (
          <ExpenseOverviewTab
            dayStatus={canSeeToday ? dayStatus : undefined}
            pnl={overviewPnL}
            summary={overviewSummary}
            intel={intel}
            periodLabel={periodLabel}
            range={range}
            onDrillCategory={drillCategory}
            onOpenLedger={openLedger}
            onOpenPnL={openPnL}
          />
        ))}

      {tab === 'ledger' &&
        (expLoading && !expenses ? (
          <Skeleton className="h-96" />
        ) : (
          <ExpenseLedgerTab
            expenses={expenses}
            meta={expMeta}
            summary={ledgerSummary}
            periodLabel={periodLabel}
            page={expPage}
            setPage={setExpPage}
            category={category}
            setCategory={setCategory}
            searchInput={expSearchInput}
            onSearchInputChange={setExpSearchInput}
            method={expMethod}
            setMethod={setExpMethod}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDir={sortDir}
            setSortDir={setSortDir}
            readOnly={ledgerReadOnly}
            onAdd={() => setAddOpen(true)}
            onEdit={e => setEditExpense(e)}
            onDelete={id => deleteExpenseMut.mutate(id)}
            onCorrectPaidOut={isAdmin ? setCorrectExpense : undefined}
            onManageCategories={isAdmin ? () => setManageCategoriesOpen(true) : undefined}
            scanAction={
              receiptScan.enabled ? (
                // A pile goes to the Receipts inbox, where it's read and reviewed in turn.
                <ReceiptScanButton onScanned={setReviewScanId} onBatch={() => navigate({ to: '/admin/receipts' })} />
              ) : undefined
            }
            onOpenReceipt={receiptScan.enabled ? setReviewScanId : undefined}
          />
        ))}

      {tab === 'pnl' &&
        (pnlLoading && !pnl ? (
          <div className="space-y-6">
            <Skeleton className="h-[116px]" />
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <Skeleton className="h-[420px] xl:col-span-2" />
              <Skeleton className="h-[420px]" />
            </div>
          </div>
        ) : (
          <ExpensePnLTab
            pnl={pnl}
            period={pnlPeriod}
            setPeriod={setPnlPeriodChoice}
            from={range.fromISO}
            to={range.toISO}
            rangeDays={range.days}
            periodLabel={periodLabel}
            onDrillCategory={drillCategory}
            onOpenLedger={openLedger}
          />
        ))}

      <AddExpenseDialog open={addOpen} onOpenChange={setAddOpen} onManageCategories={isAdmin ? () => setManageCategoriesOpen(true) : undefined} />
      <EditExpenseDialog expense={editExpense} onClose={() => setEditExpense(null)} onManageCategories={isAdmin ? () => setManageCategoriesOpen(true) : undefined} />
      <CorrectPaidOutDialog expense={correctExpense} onClose={() => setCorrectExpense(null)} />
      {receiptScan.enabled && <ReceiptReviewSheet scanId={reviewScanId} onClose={() => setReviewScanId(null)} />}
      {isAdmin && <ManageExpenseCategoriesDialog open={manageCategoriesOpen} onOpenChange={setManageCategoriesOpen} />}
    </div>
  )
}
